import React from "react";

import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import RefreshIcon from "@mui/icons-material/Refresh";
import ShareIcon from "@mui/icons-material/Share";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Chip,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";

import { getEmptyStateSx } from "src/shell/styles/themeUtils";

import { usePodMonitor } from "../../hooks/usePodMonitor";

import { ServiceGroup } from "./ServiceGroup";

interface ServicesTabProps {
  alias: string;
  /** Selected app environment slug (e.g. "dev", "staging", "production"). */
  appEnv: string;
  /** Optional CTA when no pods are found. If provided, renders a "Run Pipeline" button. */
  onRunPipeline?: () => void;
}

/**
 * Real-time pod monitoring tab.
 *
 * Groups pods into two sections:
 * - **Stack Services**: pods owned by this stack (systemAlias matches the alias).
 * - **Shared Project Services**: project-level pods shared across stacks (no systemAlias).
 *
 * Data is streamed via WebSocket — the connection indicator in the header
 * shows whether the live stream is active.
 */
export function ServicesTab({
  alias,
  appEnv,
  onRunPipeline,
}: ServicesTabProps) {
  const theme = useTheme();
  const { snapshot, loading, error, connected, refresh } = usePodMonitor(
    alias,
    appEnv,
  );

  // Split pods into stack-owned and shared project-level
  const stackPods: import("../../hooks/usePodMonitor").PodStatusSnapshot[] = [];
  const sharedPods: import("../../hooks/usePodMonitor").PodStatusSnapshot[] =
    [];

  if (snapshot?.pods) {
    for (const pod of snapshot.pods) {
      if (pod.systemAlias === alias) {
        stackPods.push(pod);
      } else if (!pod.systemAlias) {
        sharedPods.push(pod);
      }
      // Pods belonging to other stacks (systemAlias !== alias and !== undefined)
      // are excluded — they don't belong to this stack view.
    }
  }

  // Group each category by serviceName
  const groupByService = (
    pods: import("../../hooks/usePodMonitor").PodStatusSnapshot[],
  ): Record<
    string,
    import("../../hooks/usePodMonitor").PodStatusSnapshot[]
  > => {
    const grouped: Record<
      string,
      import("../../hooks/usePodMonitor").PodStatusSnapshot[]
    > = {};
    for (const pod of pods) {
      if (!grouped[pod.serviceName]) grouped[pod.serviceName] = [];
      grouped[pod.serviceName].push(pod);
    }
    return grouped;
  };

  const stackGroups = groupByService(stackPods);
  const sharedGroups = groupByService(sharedPods);

  const stackServiceNames = Object.keys(stackGroups).sort();
  const sharedServiceNames = Object.keys(sharedGroups).sort();

  const totalPods = snapshot?.pods.length ?? 0;
  const readyPods =
    snapshot?.pods.filter((p) => p.phase === "Running" && p.ready).length ?? 0;

  return (
    <Box>
      {/* Header bar */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2.5 }}
      >
        {/* Live indicator + timestamp */}
        <Tooltip
          title={connected ? "Live — real-time updates active" : "Connecting…"}
        >
          <Stack direction="row" spacing={0.75} alignItems="center">
            <FiberManualRecordIcon
              sx={{
                fontSize: 10,
                color: connected ? "success.main" : "warning.main",
              }}
            />
            <Typography variant="caption" color="text.secondary">
              {connected ? "Live" : "Connecting…"}
              {snapshot &&
              snapshot.timestamp &&
              !isNaN(new Date(snapshot.timestamp).getTime())
                ? ` · ${new Date(snapshot.timestamp).toLocaleTimeString()}`
                : ""}
            </Typography>
          </Stack>
        </Tooltip>

        {/* Summary chip + refresh */}
        <Stack direction="row" spacing={1} alignItems="center">
          {snapshot && (
            <Chip
              label={`${readyPods}/${totalPods} ready`}
              color={
                readyPods === totalPods && totalPods > 0 ? "success" : "warning"
              }
              size="small"
              variant={
                readyPods === totalPods && totalPods > 0 ? "filled" : "outlined"
              }
            />
          )}
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={refresh} disabled={loading}>
              {loading ? (
                <CircularProgress size={16} />
              ) : (
                <RefreshIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => {}}>
          {error}
        </Alert>
      )}

      {loading && !snapshot && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={32} />
        </Box>
      )}

      {!loading &&
        snapshot &&
        stackServiceNames.length === 0 &&
        sharedServiceNames.length === 0 && (
          <Box sx={getEmptyStateSx(theme)}>
            <Box
              sx={{
                width: 60,
                height: 60,
                borderRadius: 2,
                margin: "0 auto",
                mb: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "rgba(59,130,246,0.08)",
              }}
            >
              <Typography
                sx={{ fontSize: 28, color: "primary.main", opacity: 0.5 }}
              >
                ⚙
              </Typography>
            </Box>
            <Typography variant="body1" fontWeight={600} sx={{ mb: 1 }}>
              No services
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 3, maxWidth: 400, mx: "auto" }}
            >
              No pods found in namespace{" "}
              <Box component="span" fontFamily="monospace">
                {snapshot.namespace}
              </Box>
              . Deploy the stack to see running services.
            </Typography>
            {onRunPipeline && (
              <Button
                variant="contained"
                color="primary"
                size="small"
                startIcon={<PlayArrowIcon />}
                onClick={onRunPipeline}
                sx={{ borderRadius: 1.5, textTransform: "none" }}
              >
                Run pipeline
              </Button>
            )}
          </Box>
        )}

      {/* Stack Services section */}
      {stackServiceNames.length > 0 && (
        <>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Stack Services
          </Typography>
          {stackServiceNames.map((name) => (
            <ServiceGroup
              key={name}
              serviceName={name}
              pods={stackGroups[name]}
              alias={alias}
              appEnv={appEnv}
            />
          ))}
        </>
      )}

      {/* Shared Project Services section */}
      {sharedServiceNames.length > 0 && (
        <>
          <Divider sx={{ my: 3 }} />
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <ShareIcon sx={{ fontSize: 16, color: "text.secondary" }} />
            <Typography variant="subtitle2" color="text.secondary">
              Shared Project Services
            </Typography>
          </Stack>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mb: 1.5, display: "block" }}
          >
            These services are shared across all stacks in the{" "}
            <Box component="span" fontFamily="monospace">
              {snapshot?.projectAlias ?? "project"}
            </Box>{" "}
            namespace.
          </Typography>
          {sharedServiceNames.map((name) => (
            <ServiceGroup
              key={`shared-${name}`}
              serviceName={name}
              pods={sharedGroups[name]}
              alias={alias}
              appEnv={appEnv}
            />
          ))}
        </>
      )}
    </Box>
  );
}
